
import React, { useState } from 'react';
import { SystemConfig } from '@/shared/types';
import { realDataService } from '@/shared/services/realDataService';
import { Shield, AlertTriangle, RefreshCw, Save, X, CheckCircle, Trash2 } from 'lucide-react';
import { AppConfigForm, DatabaseConfigForm, PaymentConfigForm } from '@/features/admin/components/AdminConfigForms';
import { Button } from '@/shared/components/ui';

interface AdminConfigViewProps {
  initialConfig?: SystemConfig;
}

const AdminConfigView: React.FC<AdminConfigViewProps> = ({ initialConfig }) => {
  const [config, setConfig] = useState<SystemConfig>(initialConfig || realDataService.getSystemConfig());
  const [dialogState, setDialogState] = useState<{ type: 'save' | 'reset' | null }>({ type: null });
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const openSaveDialog = (e: React.FormEvent) => {
      e.preventDefault();
      setDialogState({ type: 'save' });
  };

  const openResetDialog = () => {
      setResetConfirmation('');
      setDialogState({ type: 'reset' });
  };

  const closeDialog = () => {
      setDialogState({ type: null });
      setResetConfirmation('');
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      await realDataService.adminUpdateSystemConfig(config);
      closeDialog();
      setSuccessMsg('Configuration updated successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      console.error('Failed to update configuration:', error);
      alert('Failed to update configuration. Please try again.');
    }
    setLoading(false);
  };

  const handleConfirmReset = async () => {
      if (resetConfirmation === "RESET-SYSTEM") {
          setLoading(true);
          try {
              await realDataService.adminResetSystem();
              closeDialog();
              alert('System reset successfully. The page will now reload.');
              window.location.reload();
          } catch (error) {
              console.error('Failed to reset system:', error);
              alert('Failed to reset system. Please try again.');
          }
          setLoading(false);
      }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-5">
        <div>
            <h2 className="text-lg font-semibold text-gray-900">System Configuration</h2>
            <p className="text-gray-500 text-sm">Manage platform settings</p>
        </div>

        {successMsg && (
            <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded text-sm font-medium flex items-center gap-2">
                <CheckCircle size={14}/> {successMsg}
            </div>
        )}

        <button
          onClick={openSaveDialog}
          className="bg-gray-900 text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
        >
            <Save size={14} /> Save Changes
        </button>
      </div>

      <form onSubmit={openSaveDialog} className="space-y-5">
         <AppConfigForm config={config} setConfig={setConfig} />
         <DatabaseConfigForm config={config} setConfig={setConfig} />
         <PaymentConfigForm config={config} setConfig={setConfig} />

         <div className="flex justify-end pt-2">
            <button type="submit" className="bg-gray-900 text-white px-5 py-2 rounded text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2">
               <Shield size={14} /> Apply Configuration
            </button>
         </div>
      </form>

      {/* Danger Zone */}
      <div className="mt-10 pt-6 border-t border-gray-200">
          <div className="bg-red-50 border border-red-200 rounded p-4 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-start gap-3">
                  <div className="p-2 bg-white border border-red-100 text-red-600 rounded">
                      <AlertTriangle size={18} />
                  </div>
                  <div>
                      <h3 className="text-sm font-semibold text-red-900">Danger Zone</h3>
                      <p className="text-xs text-red-700 mt-1 max-w-md">
                          Factory Reset will delete all users, transactions, and settings. The system will revert to initial installation state.
                      </p>
                  </div>
              </div>
              <button
                type="button"
                onClick={openResetDialog}
                className="bg-white border border-red-300 text-red-600 px-4 py-2 rounded text-sm font-medium hover:bg-red-600 hover:text-white transition-all flex items-center gap-2 whitespace-nowrap"
              >
                  <RefreshCw size={14} /> Factory Reset
              </button>
          </div>
      </div>
      <div className="h-8"></div>

      {/* Action Dialog */}
      {dialogState.type && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
              <div className="bg-white rounded shadow-lg max-w-md w-full overflow-hidden border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h3 className={`font-semibold text-sm flex items-center gap-2 ${dialogState.type === 'reset' ? 'text-red-600' : 'text-gray-900'}`}>
                          {dialogState.type === 'save' && <><Save size={16} className="text-gray-700"/> Save Configuration</>}
                          {dialogState.type === 'reset' && <><AlertTriangle size={16}/> Factory Reset</>}
                      </h3>
                      <button onClick={closeDialog} className="text-gray-400 hover:text-gray-600">
                          <X size={16}/>
                      </button>
                  </div>

                  <div className="p-4">
                      {dialogState.type === 'save' ? (
                          <div className="space-y-3">
                              <p className="text-sm text-gray-600">
                                  Are you sure you want to update the system configuration?
                              </p>
                              <div className="bg-blue-50 text-blue-700 p-3 rounded text-sm border border-blue-100 flex items-start gap-2">
                                  <Shield size={14} className="shrink-0"/>
                                  <span>Ensure all API keys and secrets are valid before confirming.</span>
                              </div>
                          </div>
                      ) : (
                          <div className="space-y-3">
                              <p className="text-sm text-gray-600">
                                  This action is <span className="font-semibold text-red-600">irreversible</span>. All user data, transactions, and settings will be permanently wiped.
                              </p>
                              <div>
                                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1.5">Type "RESET-SYSTEM" to confirm</label>
                                  <input
                                      type="text"
                                      className="w-full px-3 py-2 border border-red-300 rounded text-red-600 text-sm focus:border-red-400 focus:outline-none"
                                      value={resetConfirmation}
                                      onChange={(e) => setResetConfirmation(e.target.value)}
                                      placeholder="RESET-SYSTEM"
                                  />
                              </div>
                          </div>
                      )}
                  </div>

                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
                      <button
                          onClick={closeDialog}
                          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 transition-colors"
                      >
                          Cancel
                      </button>

                      {dialogState.type === 'save' ? (
                          <button
                              onClick={handleConfirmSave}
                              disabled={loading}
                              className="px-4 py-2 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50"
                          >
                              {loading ? (
                                  <>
                                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Saving...
                                  </>
                              ) : (
                                  <>
                                      <CheckCircle size={14}/> Confirm
                                  </>
                              )}
                          </button>
                      ) : (
                          <button
                              onClick={handleConfirmReset}
                              disabled={resetConfirmation !== "RESET-SYSTEM" || loading}
                              className={`px-4 py-2 text-white rounded text-sm font-medium transition-all flex items-center gap-2 disabled:opacity-50 ${
                                  resetConfirmation === "RESET-SYSTEM"
                                  ? 'bg-red-600 hover:bg-red-700'
                                  : 'bg-gray-300 cursor-not-allowed'
                              }`}
                          >
                              {loading ? (
                                  <>
                                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Resetting...
                                  </>
                              ) : (
                                  <>
                                      <Trash2 size={14}/> Reset
                                  </>
                              )}
                          </button>
                      )}
                  </div>
              </div>
          </div>
      )}
   </div>
  );
};

export default AdminConfigView;
